import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Book, BookFilters, BookStatus } from '../../types/Book';
import { sortBooks } from '../../utils/bookUtils';
import BookCard from './BookCard';
import FilterDrawer from './FilterDrawer';
import { User } from '../../services/authService';

interface BookListProps {
  books: Book[];
  loading: boolean;
  error: string | null;
  user: User | null;
  onEditBook: (book: Book) => void;
  onDeleteBook: (id: string) => void;
  onReadOnline?: (book: Book) => void;
  onStatusChange?: (book: Book, newStatus: string) => void;
  onSearchWithFilters?: (query: string, filters: {
    status?: BookStatus;
    author?: string;
    subject?: string;
    language?: string;
    publication_year?: string;
    minRating?: number;
  }) => Promise<{ books: Book[]; error: string | null }>;
  selectedSubject?: string | null;
  onClearSubject?: () => void;
  isFilterDrawerOpen: boolean;
  onFilterDrawerToggle: (open: boolean) => void;
}

const BookList: React.FC<BookListProps> = ({ books, loading, error, user, onEditBook, onDeleteBook, onReadOnline, onStatusChange, onSearchWithFilters, selectedSubject, onClearSubject, isFilterDrawerOpen, onFilterDrawerToggle }) => {
  const [filters, setFilters] = useState<BookFilters>({});
  const [sortBy, setSortBy] = useState<'title' | 'author' | 'dateAdded' | 'rating' | 'position_slot'>('dateAdded');
  const [searchQuery, setSearchQuery] = useState('');

  const [filteredBooks, setFilteredBooks] = useState<Book[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchInProgress = React.useRef<boolean>(false);
  const lastSelectedSubject = React.useRef<string | null>(null);

  // Filter books client-side
  const filterBooks = useCallback((booksToFilter: Book[], query: string, searchFilters: BookFilters) => {
    let result = [...booksToFilter];
    
    // Apply search query
    if (query) {
      const queryLower = query.toLowerCase();
      result = result.filter(book =>
        book.title.toLowerCase().includes(queryLower) ||
        book.author.toLowerCase().includes(queryLower) ||
        book.genre.toLowerCase().includes(queryLower) ||
        (book.tags && book.tags.some(tag => tag.toLowerCase().includes(queryLower)))
      );
    }
    
    // Apply filters
    if (searchFilters.status) {
      result = result.filter(book => book.status === searchFilters.status);
    }
    
    if (searchFilters.author) {
      result = result.filter(book => 
        book.author.toLowerCase().includes(searchFilters.author!.toLowerCase())
      );
    }
    
    if (searchFilters.subject) {
      result = result.filter(book => 
        book.genre.toLowerCase().includes(searchFilters.subject!.toLowerCase()) ||
        (book.tags && book.tags.some(tag => tag.toLowerCase().includes(searchFilters.subject!.toLowerCase())))
      );
    }
    
    if (searchFilters.minRating) {
      result = result.filter(book => (book.rating || 0) >= searchFilters.minRating!);
    }
    
    return result;
  }, []);

  const handleSearch = useCallback(async (query: string, searchFilters: BookFilters) => {
    // Create a unique identifier for this search call
    const searchId = `${query}-${JSON.stringify(searchFilters)}-${Date.now()}`;
    
    // Prevent duplicate concurrent searches
    if (searchInProgress.current) {
      console.log('BookList: Search already in progress, skipping duplicate call:', searchId);
      return;
    }
    
    console.log('BookList: handleSearch started:', searchId, { query, searchFilters });
    setSearchQuery(query);
    setFilters(searchFilters);
    
    // Call the API with filters if the function is available
    if (onSearchWithFilters) {
      try {
        searchInProgress.current = true;
        setSearchLoading(true);
        setSearchError(null);
        
        // Convert BookFilters to the format expected by the search API
        const apiFilters = {
          status: searchFilters.status,
          author: searchFilters.author,
          subject: searchFilters.subject,
          language: searchFilters.language,
          publication_year: searchFilters.publication_year,
          minRating: searchFilters.minRating
        };
        
        console.log('BookList: Calling onSearchWithFilters:', searchId, { query, apiFilters });
        const result = await onSearchWithFilters(query, apiFilters);
        console.log('BookList: Search completed:', searchId, 'Books found:', result.books?.length);
        if (result.error) {
          setSearchError(result.error);
        }
      } catch (err: any) {
        console.error('BookList: Error during search:', searchId, err);
        setSearchError('Failed to search books');
      } finally {
        setSearchLoading(false);
        searchInProgress.current = false;
        console.log('BookList: Search finished:', searchId);
      }
    }
  }, [onSearchWithFilters]);

  const handleFiltersChange = useCallback((newFilters: BookFilters) => {
    setFilters(newFilters);
  }, []);

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Apply subject filter when selectedSubject changes
  useEffect(() => {
    if (selectedSubject && selectedSubject !== lastSelectedSubject.current) {
      console.log('BookList: Subject selected, triggering search:', selectedSubject);
      lastSelectedSubject.current = selectedSubject;
      
      // Add a small delay to prevent rapid successive calls
      const timeoutId = setTimeout(() => {
        handleSearch('', { subject: selectedSubject });
      }, 50);
      
      return () => clearTimeout(timeoutId);
    } else if (!selectedSubject && lastSelectedSubject.current) {
      // Subject was cleared
      console.log('BookList: Subject cleared, resetting filters');
      lastSelectedSubject.current = null;
      setFilters({});
      setSearchQuery('');
    }
  }, [selectedSubject, handleSearch]);

  // Apply filters and search whenever books or filters change
  useEffect(() => {
    console.log('BookList: Applying filters to books:', {
      booksCount: books.length,
      searchQuery,
      filters,
      selectedSubject
    });
    const filtered = filterBooks(books, searchQuery, filters);
    console.log('BookList: Filtered books result:', filtered.length);
    setFilteredBooks(filtered);
  }, [books, searchQuery, filters, filterBooks, selectedSubject]);

  // Sort filtered books locally
  const sortedBooks = useMemo(() => {
    const sortByOption = filters.sortBy || sortBy;
    const sortOrder = filters.sortOrder || 'desc';
    return sortBooks(filteredBooks, sortByOption, sortOrder);
  }, [filteredBooks, sortBy, filters.sortBy, filters.sortOrder]);

  const clearAllFilters = useCallback(async () => {
    const defaultFilters = {
      sortBy: 'dateAdded' as const,
      sortOrder: 'desc' as const
    };
    setFilters(defaultFilters);
    setSearchQuery('');
    setSortBy('dateAdded');
    
    // Clear selected subject
    if (onClearSubject) {
      onClearSubject();
    }
    
    // Call API to reload all books when filters are cleared
    if (onSearchWithFilters) {
      try {
        setSearchLoading(true);
        setSearchError(null);
        const result = await onSearchWithFilters('', {});
        if (result.error) {
          setSearchError(result.error);
        }
      } catch (err: any) {
        console.error('BookList: Error clearing filters:', err);
        setSearchError('Failed to reload books');
      } finally {
        setSearchLoading(false);
      }
    }
  }, [onSearchWithFilters, onClearSubject]);

  // Show full loading only for initial load, not for search operations
  if (loading) {
    return (
      <div className="book-list-container">
        <div className="book-list-main">
          <div className="loading-state">
            <h2>📚 Loading books...</h2>
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="book-list-container">
        <div className="book-list-main">
          <div className="error-state">
            <h2>❌ Error loading books</h2>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = searchQuery || selectedSubject ||
    Object.keys(filters).some(key => key !== 'sortBy' && key !== 'sortOrder' && filters[key as keyof BookFilters]) ||
    (filters.sortBy && filters.sortBy !== 'dateAdded') ||
    (filters.sortOrder && filters.sortOrder !== 'desc') ||
    sortBy !== 'dateAdded';

  return (
    <div className="book-list-container">
      <div className="book-list-main">
        <div className="books-header-condensed">
          <div className="header-row">
            <div className="header-info">
              <h2>{selectedSubject ? `"${selectedSubject}"` : hasActiveFilters ? 'Search Results' : 'Recently Added'}</h2>
              <span className="book-count">({sortedBooks.length} books)</span>
            </div>
            <div className="header-controls">
              <div className="sort-controls-inline">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="sort-select-compact"
                  title="Sort books by"
                >
                  <option value="dateAdded">Date Added</option>
                  <option value="title">Title</option>
                  <option value="author">Author</option>
                  <option value="rating">Rating</option>
                </select>
              </div>
            </div>
          </div>

          {(selectedSubject || hasActiveFilters) && (
            <div className="filter-actions-row">
              {selectedSubject && onClearSubject && (
                <button 
                  className="clear-btn-compact clear-subject"
                  onClick={onClearSubject}
                  title="Clear subject filter"
                >
                  ✕ Clear Subject
                </button>
              )}
              
              {hasActiveFilters && (
                <button 
                  className="clear-btn-compact clear-all"
                  onClick={clearAllFilters}
                  title="Clear all filters"
                >
                  ✕ Clear All
                </button>
              )}
            </div>
          )}
        </div>

        {/* Active Filters Display - Condensed */}
        {(searchQuery || filters.status || filters.author || filters.subject || filters.language || filters.publication_year || filters.minRating) && (
          <div className="active-filters-condensed">
            {searchQuery && <span className="filter-tag-compact">"{searchQuery}"</span>}
            {filters.status && <span className="filter-tag-compact">{filters.status.replace('_', ' ')}</span>}
            {filters.author && <span className="filter-tag-compact">{filters.author}</span>}
            {filters.subject && <span className="filter-tag-compact">{filters.subject}</span>}
            {filters.language && <span className="filter-tag-compact">{filters.language}</span>}
            {filters.publication_year && <span className="filter-tag-compact">{filters.publication_year}</span>}
            {filters.minRating && <span className="filter-tag-compact">{filters.minRating}★</span>}
          </div>
        )}



        {/* Search Loading Indicator */}
        {searchLoading && (
          <div className="search-loading">
            <div className="loading-spinner-small"></div>
            <p>🔍 Searching books...</p>
          </div>
        )}

        {/* Search Error */}
        {searchError && (
          <div className="search-error">
            <p>❌ {searchError}</p>
            <button onClick={() => setSearchError(null)}>Dismiss</button>
          </div>
        )}

        <div className="books-grid">
          {sortedBooks.length === 0 && !searchLoading ? (
            <div className="empty-state">
              <p>
                {hasActiveFilters
                  ? "No books match your current search and filters. Try adjusting your criteria or clearing filters."
                  : "No recent books found. Books will appear here as they are added to the library."
                }
              </p>
            </div>
          ) : !searchLoading ? (
            sortedBooks.map(book => (
              <BookCard
                key={book.id}
                book={book}
                user={user}
                onEdit={onEditBook}
                onDelete={onDeleteBook}
                onReadOnline={onReadOnline}
                onStatusChange={onStatusChange}
              />
            ))
          ) : null}
        </div>


      </div>

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => onFilterDrawerToggle(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchQueryChange}
      />
    </div>
  );
};

export default BookList;